import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitGuestReview } from '../api/services/publicFormService';

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-2 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star
            className={`h-10 w-10 transition-colors ${
              star <= (hovered || value)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

const GuestReviewForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('p') || '';
  const guestId = searchParams.get('g') || '';

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!propertyId || !guestId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center text-gray-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-400" />
          <p className="text-lg font-medium">Invalid link</p>
          <p className="text-sm mt-1">Please use the link from your checkout email.</p>
        </div>
      </div>
    );
  }

  if (formStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-500">Your review helps future guests find great stays.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) return;
    setFormStatus('loading');
    setErrorMsg('');
    try {
      await submitGuestReview({
        property: propertyId,
        guest_id: guestId,
        rating,
        comment: comment.trim() || undefined,
      });
      setFormStatus('success');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || err?.response?.data?.error || 'Something went wrong. Please try again.');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-full mb-3">
            <Star className="h-7 w-7 text-yellow-500 fill-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Rate Your Stay</h1>
          <p className="text-gray-500 text-sm mt-1">How was your short stay experience?</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 text-center">Overall Rating <span className="text-red-500">*</span></p>
            <StarRating value={rating} onChange={setRating} />
            {rating > 0 && (
              <p className="text-center text-sm font-semibold text-yellow-600">{LABELS[rating]}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Any comments? <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              placeholder="Share what you loved or what could be improved..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-gray-50 resize-none"
            />
          </div>

          {formStatus === 'error' && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={formStatus === 'loading' || rating === 0}
            className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {formStatus === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">Powered by Social Bunkr</p>
      </div>
    </div>
  );
};

export default GuestReviewForm;
